import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class OktaService {
  private accessTokenObservable = new BehaviorSubject<string>('INVALID_OKTA_TOKEN');
  private poidObservable = new BehaviorSubject<string>('INVALID_POID');
  private crewIdObservable = new BehaviorSubject<string>('INVALID_CREWID');
  private oktaGroupsObservable = new BehaviorSubject<string[]>([]);
  private caTokenObservable = new BehaviorSubject<string>('');
  private oktaTokenExp = new BehaviorSubject<number>(0);

  constructor(
    private http: HttpClient,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
  ) {
    this.accessTokenObservable.subscribe({
      // eslint-disable-next-line complexity
      next: (token: string) => {
        const decodedJwt: any = this.parseJwt(token);
        this.poidObservable.next(decodedJwt?.vanguard?.poid);
        this.crewIdObservable.next(decodedJwt?.vanguard?.crewId);
        this.oktaGroupsObservable.next(decodedJwt?.groups);
        this.oktaTokenExp.next(decodedJwt?.exp);
      },
    });
  }

  getCrewIdObservable() {
    return this.crewIdObservable;
  }

  getToken() {
    return this.accessTokenObservable.getValue();
  }

  getPoid() {
    return this.poidObservable.getValue();
  }

  getCrewId() {
    return this.crewIdObservable.getValue();
  }

  getAccessTokenObservable() {
    return this.accessTokenObservable;
  }

  getOktaTokenExp() {
    return this.oktaTokenExp.getValue();
  }

  getPoidObservable() {
    return this.poidObservable;
  }

  getOktaGroupsObservable() {
    return this.oktaGroupsObservable;
  }

  async performLogin(originalUri: string) {
    if (this.oktaAuth.isLoginRedirect()) {
      try {
        await this.oktaAuth.handleLoginRedirect();
      } catch (e) {
        return {
          isAuthenticated: false,
          userClaims: null,
        };
      }
    } else if (!(await this.oktaAuth.isAuthenticated())) {
      await this.oktaAuth.signInWithRedirect({ originalUri: originalUri });
    }
    return {
      isAuthenticated: await this.oktaAuth.isAuthenticated(),
      userClaims: await this.oktaAuth.getUser(),
    };
  }

  async isAuthenticated() {
    return await this.oktaAuth.isAuthenticated();
  }

  private parseJwt(token: string): any {
    try {
      const base64Url: string = token.split('.')[1];
      const base64: string = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload: string = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  }

  /**
   * Get CA token as observable (read-only stream)
   */
  getCaTokenObservable() {
    return this.caTokenObservable.asObservable();
  }

  /**
   * Get CA token BehaviorSubject (for internal updates)
   * @internal Use getCaTokenObservable() for subscriptions
   */
  getCaTokenSubject() {
    return this.caTokenObservable;
  }

  /**
   * Get current CA token value synchronously
   */
  getCaToken() {
    return this.caTokenObservable.getValue();
  }

  async getUserClaims() {
    return await this.oktaAuth.getUser();
  }
}
